import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { QueryProvider } from './providers/query-provider';
import { ThemeProvider } from './components/theme-provider';
import { CompanyProvider } from './providers/company-provider';
import { Toaster } from 'sonner';
import { AuthLayoutWrapper } from './components/auth-layout-wrapper';
import { LoginPage } from './pages/LoginPage';
import { PasswordResetPage } from './pages/PasswordResetPage';
import { PortalEntry, PortalChat } from './components/portal';
import { PageLoader } from './components/page-loader';

// Lazy load major page components for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ConversationsPage = lazy(() => import('./pages/ConversationsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AppDetailPage = lazy(() => import('./pages/AppDetailPage'));
const CustomerProfilePage = lazy(() => import('./pages/CustomerProfilePage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const PlatformAdminPage = lazy(() => import('./pages/PlatformAdmin'));
const PlatformCompanyPage = lazy(() => import('./pages/PlatformCompany'));
const PlatformUserPage = lazy(() => import('./pages/PlatformUser'));
const PlatformAppPage = lazy(() => import('./pages/PlatformApp'));
const ProfilePage = lazy(() => import('./pages/Profile'));
const BillingSetupPage = lazy(() => import('./pages/billing/BillingSetup').then(module => ({ default: module.BillingSetup })));
const BillingSuccessPage = lazy(() => import('./pages/BillingSuccess'));

function App() {
    return (
        <QueryProvider>
            <ThemeProvider>
                <Routes>
                    {/* Portal routes - no auth required */}
                    <Route path="/portal" element={<PortalEntry />} />
                    <Route path="/portal/chat" element={<PortalChat />} />
                    
                    {/* Authenticated routes */}
                    <Route path="/*" element={
                        <AuthLayoutWrapper>
                            <CompanyProvider>
                                <Suspense fallback={<PageLoader />}>
                                    <Routes>
                                        <Route path="/" element={<HomePage />} />
                                        <Route path="/dashboard" element={<DashboardPage />} />
                                        <Route path="/login" element={<LoginPage />} />
                                        <Route path="/password-reset/:hash" element={<PasswordResetPage />} />
                                        <Route path="/conversations" element={<ConversationsPage />} />
                                        <Route path="/conversations/:id" element={<ConversationsPage />} />
                                        <Route path="/customers" element={<CustomersPage />} />
                                        <Route path="/customers/:customerId" element={<CustomerProfilePage />} />
                                        <Route path="/reports" element={<ReportsPage />} />
                                        <Route path="/profile" element={<ProfilePage />} />
                                        <Route path="/billing/setup" element={<BillingSetupPage />} />
                                        <Route path="/billing/success" element={<BillingSuccessPage />} />
                                        <Route path="/admin" element={<AdminPage />} />
                                        <Route path="/admin/apps/:id" element={<AppDetailPage />} />
                                        <Route path="/platform-admin" element={<PlatformAdminPage />} />
                                        <Route path="/platform-admin/company/:companyId" element={<PlatformCompanyPage />} />
                                        <Route path="/platform-admin/user/:userId" element={<PlatformUserPage />} />
                                        <Route path="/platform-admin/app/:appId" element={<PlatformAppPage />} />
                                    </Routes>
                                </Suspense>
                            </CompanyProvider>
                        </AuthLayoutWrapper>
                    } />
                </Routes>
                <Toaster />
            </ThemeProvider>
        </QueryProvider>
    );
}

export default App; 